import {
  Article,
  LegacyReportSummaryItem,
  useGetLegacyArticles,
} from "../../api/LegacyReportsApi";
import { useState, useEffect } from "react";
import LegacyArticleCard from "./LegacyArticleCard";
import MonthlyList from "../common/MonthlyList";

function LegacyArticleView({
  report,
  showRelevantOnlyArticles,
  enabledBehaviors,
  enabledKeywords,
}: {
  report: LegacyReportSummaryItem;
  showRelevantOnlyArticles: boolean;
  enabledBehaviors: string[];
  enabledKeywords: string[];
}) {
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { mutateAsync: getArticles, isLoading: isInitialLoading } =
    useGetLegacyArticles(report.reportId);

  // Load initial articles
  useEffect(() => {
    const loadArticles = async () => {
      const articles = await getArticles({
        reportUUID: report.reportId,
        page: 1,
      });
      setAllArticles(articles);
      setHasMore(articles.length === 20); // Assuming 20 is the page size
    };
    loadArticles();
  }, [report.reportId, getArticles]);

  // Handle scroll loading
  useEffect(() => {
    const handleScroll = async () => {
      if (isLoading || !hasMore) return;

      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 1000;

      if (scrolledToBottom) {
        setIsLoading(true);
        const nextPage = page + 1;
        const newArticles = await getArticles({
          reportUUID: report.reportId,
          page: nextPage,
        });

        setAllArticles((prev) => [...prev, ...newArticles]);
        setPage(nextPage);
        setHasMore(newArticles.length === 20);
        setIsLoading(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [getArticles, hasMore, isLoading, page, report.reportId]);

  // Apply filters to articles
  useEffect(() => {
    if (!allArticles || allArticles.length === 0) {
      setFilteredArticles([]);
      return;
    }

    const filteredArticles = allArticles.filter((article) => {
      const hasEnabledBehavior = enabledBehaviors.some((behavior) =>
        article.flaggedSensitivities.includes(behavior)
      );
      const hasEnabledKeyword = enabledKeywords.some((keyword) =>
        [
          ...article.flaggedKeywords,
          ...article.flaggedStemmableKeywords,
        ].includes(keyword)
      );
      const isRelevantOnly =
        article.relevant && !hasEnabledBehavior && !hasEnabledKeyword;

      return (
        (isRelevantOnly && showRelevantOnlyArticles) ||
        hasEnabledBehavior ||
        hasEnabledKeyword
      );
    });

    setFilteredArticles(filteredArticles);
  }, [
    allArticles,
    enabledBehaviors,
    enabledKeywords,
    showRelevantOnlyArticles,
    setFilteredArticles,
  ]);

  if (isInitialLoading && !allArticles.length) {
    return (
      <div className="p-4 mb-4 rounded">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-[#E0E8E8] rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-[#E0E8E8] rounded"></div>
            <div className="h-20 bg-[#E0E8E8] rounded"></div>
            <div className="h-20 bg-[#E0E8E8] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!filteredArticles.length) {
    return (
      <div className="p-4 mb-4 rounded">
        <h2 className="text-[15px] font-roboto font-normal tracking-[0.3px] text-[#84969f]">
          Articles
        </h2>
        <p className="text-gray-500 mt-4">
          No articles matching the selected filters
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 mb-4 rounded">
      <h2 className="text-[15px] font-roboto font-normal tracking-[0.3px] text-[#84969f]">
        Articles
      </h2>
      <div className="mt-4 w-full space-y-4">
        <MonthlyList
          items={filteredArticles}
          renderItem={(article) => <LegacyArticleCard article={article} />}
          getItemDate={(article) =>
            new Date(article.contentFamaDate ?? new Date())
          }
          columns={1}
        />
      </div>
      {isLoading && (
        <div className="flex justify-center py-4">
          <div className="animate-pulse text-gray-500">
            Loading more articles...
          </div>
        </div>
      )}
    </div>
  );
}

export default LegacyArticleView;
